import React from "react";
import { useLocation } from "react-router-dom";
import { HOME_STATIC_VISIBLE, IS_PRERENDER, beginPrerenderHandoff, completePrerenderHandoff, } from "./prerender";
/**
 * Старт приложения поверх снимка пререндера (см. docs/SEO.md, «Подмена статики»).
 *
 * nginx отдаёт готовый HTML всем посетителям, поэтому к моменту запуска бандла в
 * `#root` уже лежит статика. Здесь решается, что с ней делать:
 *   - "handoff" — статика остаётся на экране, React рендерится в контейнер вне
 *     документа и одним кадром заменяет её, когда страница готова;
 *   - "discard" — статика этому посетителю не подходит (мини-апп, мобильный
 *     шелл, сессия аккаунта): чистим `#root` и стартуем как раньше, через
 *     лоадер;
 *   - "none" — обычный SPA-шелл без снимка.
 *
 * Раннее решение «спрятать статику» принимает инлайн-скрипт в public/index.html
 * (ДО первой отрисовки, по localStorage и launch-URL) и помечает `<html>`
 * классом `vz-static-off`. Здесь оно перепроверяется точными предикатами
 * приложения — двойной пояс на случай, если скрипт чего-то не знал.
 */
/** Атрибуты снимка на `<html>`, их ставит scripts/prerender.js. */
const ATTR_MARK = "data-prerendered";
const ATTR_PATH = "data-prerender-path";
const ATTR_LAYOUT = "data-prerender-layout";
/** Класс инлайн-скрипта из index.html: статика скрыта до старта приложения. */
const CLASS_STATIC_OFF = "vz-static-off";
const normalizePath = (pathname) => pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
/** Чистая развилка (без window) — покрыта тестами. */
export function resolveSnapshotMode(input) {
    const { snapshot } = input;
    if (!snapshot)
        return "none";
    if (input.staticHidden)
        return "discard";
    // nginx отдаёт снимок по точному пути; другое — значит, адрес уже переписан
    // (редирект до старта роутера) и статика не про эту страницу.
    if (normalizePath(input.pathname) !== snapshot.path)
        return "discard";
    if (input.isMiniApp || input.isDebugLaunch)
        return "discard";
    if (snapshot.layout === "shell" && input.isMiniAppShell)
        return "discard";
    // Страница шелла в статике — с гостевым боковым меню. Снимок "/" — лендинг
    // (он вне шелла), а аккаунт с "/" уходит в студию: ему он тоже не подходит.
    if ((snapshot.layout === "shell" || snapshot.path === "/") &&
        input.hasAccountSession) {
        return "discard";
    }
    // Главная: аварийный выключатель показа снимка людям (HOME_STATIC_VISIBLE).
    if (snapshot.path === "/" && !(input.homeStaticVisible ?? HOME_STATIC_VISIBLE)) {
        return "discard";
    }
    return "handoff";
}
/** Снимок в текущем документе (или null — обычный SPA-шелл / сам пререндер). */
export function readSnapshot(rootEl) {
    if (IS_PRERENDER)
        return null;
    const html = document.documentElement;
    if (!html.hasAttribute(ATTR_MARK) || !rootEl.firstElementChild)
        return null;
    return {
        path: html.getAttribute(ATTR_PATH) || "",
        layout: html.getAttribute(ATTR_LAYOUT) === "standalone" ? "standalone" : "shell",
    };
}
export const isStaticHidden = () => document.documentElement.classList.contains(CLASS_STATIC_OFF);
/**
 * Признак сессии аккаунта без запроса к бэкенду — те же ключи, что читает
 * инлайн-гейт: Bearer-токен мини-аппа или флаг авторизации в persist-сторе
 * пользователя. localStorage может быть недоступен — тогда «сессии нет».
 */
export function hasStoredAccountSession() {
    try {
        if (localStorage.getItem("accessToken"))
            return true;
        const stored = JSON.parse(localStorage.getItem("user-storage") || "null");
        return stored?.state?.isAuthenticated === true;
    }
    catch {
        return false;
    }
}
/** Инлайн-гейт больше не нужен: снять слушатель кликов, вернуть запомненный клик. */
function takeStaticClick() {
    const gate = window.__vzStatic;
    if (!gate)
        return null;
    try {
        gate.stop();
    }
    catch {
        /* гейт — best-effort */
    }
    window.__vzStatic = undefined;
    return gate.click;
}
function clearSnapshotMarkers() {
    const html = document.documentElement;
    html.removeAttribute(ATTR_MARK);
    html.removeAttribute(ATTR_PATH);
    html.removeAttribute(ATTR_LAYOUT);
    html.classList.remove(CLASS_STATIC_OFF);
}
/** Убрать статику и её метки: дальше приложение стартует как без снимка. */
export function discardSnapshot(rootEl) {
    takeStaticClick();
    rootEl.textContent = "";
    clearSnapshotMarkers();
    return rootEl;
}
/** Путь узла от корня индексами детей — чтобы найти «тот же» узел в живом дереве. */
function indexPath(root, el) {
    const path = [];
    let node = el;
    while (node && node !== root) {
        const parent = node.parentElement;
        if (!parent)
            return null;
        path.unshift(Array.prototype.indexOf.call(parent.children, node));
        node = parent;
    }
    return node === root ? path : null;
}
/** Узел живого дерева по пути индексов из статики (или undefined). */
function resolvePath(root, path) {
    let node = root;
    for (const i of path)
        node = node?.children[i];
    return node;
}
const normalizeText = (el) => (el.textContent || "").replace(/\s+/g, " ").trim().slice(0, 80);
function captureUiState(root) {
    const active = document.activeElement;
    const focusPath = active && active !== root && root.contains(active) ? indexPath(root, active) : null;
    return {
        details: Array.from(root.querySelectorAll("details")).map((d) => d.open),
        focus: focusPath && active ? { path: focusPath, tag: active.tagName } : null,
    };
}
function restoreUiState(root, state) {
    const details = root.querySelectorAll("details");
    // Только если набор тот же: иначе индексы указывают на другие блоки.
    if (details.length === state.details.length) {
        details.forEach((d, i) => {
            if (d.open !== state.details[i])
                d.open = state.details[i];
        });
    }
    if (state.focus) {
        const el = resolvePath(root, state.focus.path);
        if (el instanceof HTMLElement && el.tagName === state.focus.tag) {
            el.focus({ preventScroll: true });
        }
    }
}
/**
 * Повторить клик, сделанный по кнопке статики до подмены (обработчиков тогда
 * ещё не было). Best-effort: только если по тому же пути в живом дереве лежит
 * элемент с тем же тегом и текстом — иначе это другая кнопка, и лучше ничего.
 */
function replayStaticClick(root, click) {
    if (!click)
        return;
    const el = resolvePath(root, click.path);
    if (!(el instanceof HTMLElement))
        return;
    if (el.tagName !== click.tag || normalizeText(el) !== click.text)
        return;
    el.click();
}
/**
 * Человек мог успеть прокрутить статику. Окно скроллят лендинги, внутренний
 * контейнер (`.contentBody`) — страницы внутри шелла; запоминаем оба вида.
 */
function captureScroll(root) {
    const inner = [];
    root.querySelectorAll("*").forEach((el) => {
        if (el.scrollTop > 0 || el.scrollLeft > 0) {
            const path = indexPath(root, el);
            if (path)
                inner.push({ path, top: el.scrollTop, left: el.scrollLeft });
        }
    });
    return { x: window.scrollX, y: window.scrollY, inner };
}
function restoreScroll(root, state) {
    for (const { path, top, left } of state.inner) {
        const node = resolvePath(root, path);
        if (node) {
            node.scrollTop = top;
            node.scrollLeft = left;
        }
    }
    if (window.scrollX !== state.x || window.scrollY !== state.y) {
        window.scrollTo(state.x, state.y);
    }
}
/**
 * Контейнер, в который монтировать React.
 *   - "none"    → сам `#root`;
 *   - "discard" → очищенный `#root` (дальше всё как без снимка);
 *   - "handoff" → новый `<div id="root">` ВНЕ документа. В документе остаётся
 *     статика, поэтому ни дублей контента, ни второго H1 не бывает ни на кадр;
 *     замена — одна DOM-операция `replaceWith`.
 */
export function prepareAppContainer(rootEl, mode) {
    if (mode === "none")
        return rootEl;
    if (mode === "discard")
        return discardSnapshot(rootEl);
    const live = document.createElement("div");
    live.id = rootEl.id;
    beginPrerenderHandoff(live, () => {
        const click = takeStaticClick();
        const scroll = captureScroll(rootEl);
        const ui = captureUiState(rootEl);
        rootEl.replaceWith(live);
        clearSnapshotMarkers();
        restoreUiState(live, ui);
        restoreScroll(live, scroll);
        replayStaticClick(live, click);
    });
    return live;
}
/**
 * Приложение ушло с пререндеренного адреса раньше, чем страница сообщила о
 * готовности (`/` → /generate-song у залогиненного) — статика
 * больше не про этот экран, подменяем сразу. Рендерится внутри роутера.
 */
export const PrerenderRouteGuard = () => {
    const { pathname } = useLocation();
    const initialPath = React.useRef(pathname);
    React.useEffect(() => {
        if (pathname !== initialPath.current)
            completePrerenderHandoff();
    }, [pathname]);
    return null;
};
